"use client"

import { AnalyzeResponse, EnrichmentResponse, GenerationResponse } from '@/types'
import { GeneratedContentType } from '@/types/content'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.contentcopycat.com'

interface ApiErrorResponse {
  message: string;
  code?: string;
}

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  console.log('📥 API Response:', {
    status: response.status,
    statusText: response.statusText,
    headers: Object.fromEntries(response.headers.entries()),
    timestamp: new Date().toISOString()
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'An error occurred' })) as ApiErrorResponse;
    console.error('❌ API Error:', {
      status: response.status,
      error,
      timestamp: new Date().toISOString()
    });
    throw new ApiError(response.status, error.message || 'An error occurred');
  }

  const data = await response.json() as T;
  console.log('✅ API Success:', {
    endpoint: response.url,
    responseData: data,
    timestamp: new Date().toISOString()
  });
  return data;
}

export const api = {
  analyzeUrl: async (url: string): Promise<AnalyzeResponse> => {
    console.log('🚀 Analyzing URL:', {
      url,
      endpoint: `${API_BASE_URL}/api/process`,
      timestamp: new Date().toISOString()
    });

    try {
      const response = await fetch(`${API_BASE_URL}/api/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url }),
      });

      const result = await handleResponse<AnalyzeResponse>(response);
      console.log('📊 Analysis Result:', {
        success: result.success,
        contentId: result.storedContentId,
        type: result.type,
        hasTranscript: !!result.content?.transcript,
        timestamp: new Date().toISOString()
      });
      return result;
    } catch (error) {
      console.error('💥 Analysis Error:', {
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        timestamp: new Date().toISOString()
      });
      throw error;
    }
  },

  executeEnrichment: async (contentId: string, selectedOptions: string[]): Promise<EnrichmentResponse> => {
    console.log('🚀 Executing Enrichment:', {
      contentId,
      selectedOptions,
      endpoint: `${API_BASE_URL}/api/enrich/execute`,
      timestamp: new Date().toISOString()
    });

    try {
      const response = await fetch(`${API_BASE_URL}/api/enrich/execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ contentId, selectedOptions }),
      });

      const result = await handleResponse<EnrichmentResponse>(response);
      console.log('📊 Enrichment Result:', {
        success: result.success,
        contentId: result.contentId,
        hasContent: !!result.enrichedContent?.content,
        timestamp: new Date().toISOString()
      });
      return result;
    } catch (error) {
      console.error('💥 Enrichment Error:', {
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        timestamp: new Date().toISOString()
      });
      throw error;
    }
  },

  generateContent: async (contentId: string, type: GeneratedContentType): Promise<GenerationResponse> => {
    console.log('🚀 Generating Content:', {
      contentId,
      type,
      endpoint: `${API_BASE_URL}/api/generation/generate/${type}`,
      timestamp: new Date().toISOString()
    });

    try {
      const response = await fetch(`${API_BASE_URL}/api/generation/generate/${type}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ contentId }),
      });

      const result = await handleResponse<GenerationResponse>(response);
      console.log('📊 Generation Result:', {
        success: result.success,
        generatedTypes: result.metadata?.generatedTypes,
        timestamp: new Date().toISOString()
      });
      return result;
    } catch (error) {
      console.error('💥 Generation Error:', {
        error: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        timestamp: new Date().toISOString()
      });
      throw error;
    }
  },
} 